/**
 * Glue between the drafter's thread feed and the Bear iframe sandbox
 * (SUP-273 P2, hearth's `use-sandbox-tools` shape). The DO assembles
 * eval / run / source / show and the checkpoint and jig tools with NO
 * `execute`, so a call parks at `input-available`; this hook scans the feed,
 * runs the call in the sandbox, and POSTs the result back. An eval's own
 * `tools` reach the same browser tools through `runPageTool`. Failures ride error-shaped OUTPUTS (`{error}`),
 * never an unresolved part — an unanswered call bricks the thread
 * server-side, and an errored output lets auto-continuation fire.
 */
import type { AutomergeUrl, Repo } from "@automerge/automerge-repo";
import { evalInputSchema } from "apprentices/tools/eval";
import type { ShowInput } from "apprentices/tools/show";
import { BEAR_PAGE } from "apprentices/worker/agent-tools";
import { CommandRefused } from "den/client/use-chat-feed";
import { resolveConsoleInput } from "forest/builtin-apps/typescript-console/input";
import type { useAppHostRegistry } from "forest/sandbox/AppHostRegistry";
import type { AgentToolResult } from "playground/agent-tools/contract";
import type { AppTool } from "playground/apps/manifest";
import {
  appRunResult,
  appStructuredOutput,
  mcpData,
  mcpError,
} from "playground/apps/tool-mcp";
import {
  appToolName,
  pageToolModule,
  parseAppToolName,
  routeAppToolCall,
} from "playground/apps/tool-routing";
import { resolveModelEntry } from "playground/builder/entry";
import {
  captureAndCreateCheckpoint,
  listCheckpointsForDir,
  revertToCheckpoint,
} from "playground/checkpoints";
import type { Playground } from "playground/hal/index";
import type { CheckpointsRefDoc } from "playground/project";
import { useCallback, useMemo, useRef } from "react";
import { invariant } from "ts-invariant";

import type { useBearSandbox } from "@/components/BearSandboxProvider";
import {
  clearSandboxContext,
  evaluateInSandbox,
  frameNodes,
  getSandboxConsoleEntries,
  queryGraph,
  type SandboxConsoleEntry,
  selectNodes,
} from "@/lib/bear-sandbox-bridge";
import { bearAgentTools, type PageTool } from "@/panes/bear-agent-tools";
import { sourceChainFrames } from "@/panes/inspector-utils";
import type { Project } from "@/types/projects";

const CLIENT_EXECUTED_TOOLS = new Set([
  "eval",
  "executeScript",
  "run",
  "source",
  "show",
  // The checkpoint log lives in the client Automerge repo, so these run here
  // for the same reason the sandbox tools do: this is where the state is.
  "checkpoint",
  "list_checkpoints",
  "restore_checkpoint",
]);

const CHECKPOINT_TOOLS = new Set([
  "checkpoint",
  "list_checkpoints",
  "restore_checkpoint",
]);

export interface CheckpointCtx {
  repo: Repo | null;
  indexUrl: AutomergeUrl;
  url: AutomergeUrl | undefined;
}

/**
 * The drafter's three checkpoint tools, run against the client's Automerge
 * repo. These are the same primitives Hearth's tool bridge calls, so both
 * agents get identical behaviour from identically-worded tools.
 */
async function runCheckpointTool(
  name: string,
  rawInput: unknown,
  ctx: CheckpointCtx,
  files: Project["files"],
) {
  const { repo, indexUrl, url } = ctx;
  invariant(repo, "Automerge repo is not available");
  invariant(url, "Checkpoints are still initializing for this project");
  const input = (rawInput ?? {}) as {
    path?: string;
    description?: string;
    id?: string;
  };

  if (name === "checkpoint") {
    invariant(typeof input.path === "string", "checkpoint needs a path");
    invariant(
      typeof input.description === "string",
      "checkpoint needs a description",
    );
    const ref = await captureAndCreateCheckpoint(
      repo,
      url,
      files,
      input.path,
      input.description,
    );
    return { id: ref.id, path: ref.path };
  }

  const refsHandle = await repo.find<CheckpointsRefDoc>(url);
  await refsHandle.whenReady();

  if (name === "list_checkpoints") {
    invariant(typeof input.path === "string", "list_checkpoints needs a path");
    const checkpoints = listCheckpointsForDir(refsHandle.doc(), input.path).map(
      (c) => ({
        id: c.id,
        description: c.description,
        createdAt: c.createdAt,
        path: c.path,
        ...(c.parent !== undefined ? { parent: c.parent } : {}),
      }),
    );
    return { checkpoints, active: refsHandle.doc()?.head };
  }

  invariant(typeof input.id === "string", "restore_checkpoint needs an id");
  const target = refsHandle.doc()?.checkpoints.find((c) => c.id === input.id);
  invariant(target, `checkpoint not found: ${input.id}`);
  const result = await revertToCheckpoint({
    repo,
    indexUrl,
    checkpointsDocUrl: url,
    checkpoint: target,
    message: `Restored checkpoint: ${target.description}`,
  });
  return { restored: result.restored.length, path: target.path };
}

const formatConsoleEntries = (entries: SandboxConsoleEntry[]) =>
  entries
    .filter((entry) => entry.type !== "input")
    .map((entry) => {
      const timestamp = new Date(entry.timestamp).toISOString();
      switch (entry.type) {
        case "log":
          return `${timestamp} [${entry.level.toUpperCase()}] ${entry.args.join(" ")}`;
        case "result":
          return ""; // We already include the result in the payload
        case "error":
          return `${timestamp} [ERROR] ${entry.value}`;
      }
      return null;
    })
    .filter((line): line is string => Boolean(line))
    .join("\n");

/**
 * Where a node came from, for the `source` tool: the JSX call sites the engine
 * recorded for it, resolved to workspace paths the agent can `read`. Same
 * resolution the inspector's "jump to source" uses.
 */
async function resolveSource(input: unknown, playground: Playground) {
  invariant(
    typeof input === "object" &&
      input !== null &&
      "fqn" in input &&
      typeof input.fqn === "string",
    "source tool input must have a string `fqn` field",
  );
  const fqn = input.fqn;
  const [node] = await queryGraph([["nodes", fqn]]);
  invariant(
    node,
    `No node "${fqn}" in the rendered model. Check the fqn, and run the model if it hasn't been built.`,
  );
  const entries = await playground.listFiles(undefined, { recursive: true });
  const paths = new Set(entries.map((e) => e.path));
  const frames = sourceChainFrames(node.source, (path) => paths.has(path));
  return { frames };
}

interface PendingToolCall {
  toolCallId: string;
  toolName: string;
  input: unknown;
}

/** Sandbox-tool parts parked at `input-available` — the client's to run. */
function pendingToolCalls(
  messages: { parts?: unknown[] }[],
): PendingToolCall[] {
  const calls: PendingToolCall[] = [];
  for (const message of messages) {
    for (const part of message.parts ?? []) {
      const p = part as {
        type: string;
        state?: string;
        toolCallId?: string;
        input?: unknown;
      };
      if (p.state !== "input-available" || !p.toolCallId) continue;
      const toolName = p.type.startsWith("tool-")
        ? p.type.slice("tool-".length)
        : undefined;
      if (!toolName) continue;
      if (!CLIENT_EXECUTED_TOOLS.has(toolName) && !parseAppToolName(toolName))
        continue;
      calls.push({ toolCallId: p.toolCallId, toolName, input: p.input });
    }
  }
  return calls;
}

/** A call we've handled: `answered` re-POSTs its output if the feed still
 *  shows the part unresolved (a lost POST heals on the next scan). */
type CallRecord =
  | { state: "running" }
  | { state: "answered"; output: unknown; inFlight: boolean };

export interface SandboxToolDeps {
  projectId: string;
  /** The chat whose host serves an agent eval's tools. */
  chatId: string | null;
  taskId?: string;
  run: ReturnType<typeof useBearSandbox>["run"];
  files: Project["files"];
  /** The client repo the checkpoint log lives in. */
  checkpoints: CheckpointCtx;
  /** Open the pane a `show` call names and reveal within it; returns what is now on screen. */
  show: (input: ShowInput) => Promise<string>;
  /** The mount-aware filesystem: what `read` and the editors can open. */
  playground: Playground;
  /** The project's running jigs, which answer jig tool calls. */
  jigHosts: ReturnType<typeof useAppHostRegistry>;
}

async function projectModelEntry(playground: Playground): Promise<string> {
  return resolveModelEntry(async (path) => {
    const file = await playground.open(path);
    try {
      return file.kind === "text" ? await file.read() : "";
    } finally {
      await file.close();
    }
  });
}

/**
 * The `show` tool: the route opens and reveals; the model kind also drives
 * the 3D view through the bridge. Checks the target exists first, so the
 * agent hears "no such file" instead of watching nothing happen.
 */
async function runShow(rawInput: unknown, deps: SandboxToolDeps) {
  const input = rawInput as ShowInput;
  if (input.kind === "model") {
    for (const fqn of input.fqns) {
      const [node] = await queryGraph([["nodes", fqn]]);
      invariant(
        node,
        `No node "${fqn}" in the rendered model. Check the fqn, and run the model if it hasn't been built.`,
      );
    }
    const shown = await deps.show(input);
    selectNodes(input.fqns);
    frameNodes(input.fqns);
    return { shown };
  }
  // Through the HAL, not `files`: a mounted file (node_modules, a skill) is
  // absent from the index until traversed, and it opens fine. The resolved
  // path carries `open`'s extension fallback, so the tab matches the file.
  const resolved = await deps.playground.resolve(input.path);
  invariant(resolved, `No file at "${input.path}"`);
  return { shown: await deps.show({ ...input, path: resolved.path }) };
}

/** A jig tool: run the export in the jig's page, launching the jig if it
 *  isn't open. Resolves to the run's output, `{ error }` when it failed. */
async function runJigTool(
  toolName: string,
  input: unknown,
  deps: SandboxToolDeps,
): Promise<{ output: unknown; tool?: AppTool }> {
  // A call parked before a reload can arrive before the index catches up.
  await deps.playground.apps.ready();
  const route = routeAppToolCall(toolName, deps.playground.apps.entries());
  // The server only offers page tools, so anything else is a jig since removed.
  if (route.kind !== "page") {
    return {
      output: { error: `No jig in this project provides ${toolName}.` },
    };
  }
  try {
    const result = await deps.jigHosts
      .getOrCreateHost(route.path)
      .callTool(route.toolName, input, pageToolModule(route.path, route.tool));
    return {
      output: result.ok ? result.output : { error: result.error },
      tool: route.tool,
    };
  } catch (err) {
    return {
      output: { error: err instanceof Error ? err.message : String(err) },
      tool: route.tool,
    };
  }
}

/** One of the lead's own browser tools; throws when it fails. */
async function runBearTool(
  name: string,
  input: unknown,
  deps: SandboxToolDeps,
): Promise<unknown> {
  if (name === "run")
    return deps.run({
      remote: true,
      entryPoint: await projectModelEntry(deps.playground),
      exportName: "",
      props: {},
    });
  if (CHECKPOINT_TOOLS.has(name))
    return runCheckpointTool(name, input, deps.checkpoints, deps.files);
  if (name === "source") return resolveSource(input, deps.playground);
  invariant(name === "show", `${name} is not a browser tool`);
  return runShow(input, deps);
}

/**
 * A tool eval reached that runs in this page, as MCP: the lead's own browser
 * tools all declare an output schema, and a jig's says whether it has one.
 */
async function runPageTool(
  page: PageTool,
  input: Record<string, unknown> | undefined,
  deps: SandboxToolDeps,
): Promise<AgentToolResult> {
  if (page.appId === BEAR_PAGE) {
    try {
      return {
        result: mcpData(await runBearTool(page.tool, input ?? {}, deps)),
        structured: true,
      };
    } catch (err) {
      return {
        result: mcpError(err instanceof Error ? err.message : String(err)),
        structured: true,
      };
    }
  }
  const { output, tool } = await runJigTool(
    appToolName(page.appId, page.tool),
    input ?? {},
    deps,
  );
  return {
    result: appRunResult(output),
    structured: appStructuredOutput(tool?.outputSchema) !== undefined,
  };
}

/** What `eval` runs: its code, or a script file's default export with args. */
async function evalSource(
  input: unknown,
  deps: SandboxToolDeps,
): Promise<string> {
  const { filePath, args, ...rest } = evalInputSchema.parse(input);
  const resolved = await resolveConsoleInput(
    {
      ...rest,
      filePath,
      // A script's handler gets the project it runs in.
      args: filePath
        ? {
            ...args,
            projectId: deps.projectId,
            ...(deps.taskId ? { taskId: deps.taskId } : {}),
          }
        : args,
    },
    (path) => fetchScript(path, deps),
  );
  if (!resolved.ok) throw new Error(resolved.error);
  return resolved.code;
}

/** A script by its `read` path, mounted skills included. */
async function fetchScript(path: string, deps: SandboxToolDeps) {
  const params = new URLSearchParams({ path, projectId: deps.projectId });
  if (deps.taskId) params.set("taskId", deps.taskId);
  const res = await fetch(`/api/apprentices/file-content?${params.toString()}`);
  if (!res.ok) {
    // statusText is empty over HTTP/2; the response body is what actually
    // carries "File not found" / the failure reason.
    const body = await res.text().catch(() => "");
    const reason = body.trim() || res.statusText || `HTTP ${res.status}`;
    throw new Error(
      `Failed to fetch script "${path}" (status ${res.status}): ${reason}`,
    );
  }
  return res.text();
}

export interface SandboxToolHandlers {
  /** Scan the feed's messages and drive any pending sandbox call. */
  onMessages: (messages: { parts?: unknown[] }[]) => void;
}

/** Run one pending call against the sandbox; the return value is the tool
 *  OUTPUT (error-shaped on failure, never a throw). */
async function executeToolCall(
  toolCall: PendingToolCall,
  deps: SandboxToolDeps,
): Promise<unknown> {
  if (parseAppToolName(toolCall.toolName))
    return (await runJigTool(toolCall.toolName, toolCall.input, deps)).output;

  if (toolCall.toolName !== "eval" && toolCall.toolName !== "executeScript") {
    try {
      return await runBearTool(toolCall.toolName, toolCall.input, deps);
    } catch (err) {
      return { error: err instanceof Error ? err.message : String(err) };
    }
  }

  // eval / executeScript share the console capture.
  const startIndex = getSandboxConsoleEntries().length;
  try {
    let result;
    if (toolCall.toolName === "executeScript") {
      // Calls parked before `eval({ filePath })` replaced this tool.
      const { clearContext: shouldClear = true, ...input } = toolCall.input as {
        clearContext?: boolean;
      };
      if (shouldClear) await clearSandboxContext();
      result = await evaluateInSandbox(await evalSource(input, deps));
    } else {
      const { chatId } = deps;
      invariant(chatId, "An agent eval runs in a chat.");
      result = await evaluateInSandbox(await evalSource(toolCall.input, deps), {
        execution: toolCall.toolCallId,
        tools: bearAgentTools(
          {
            projectId: deps.projectId,
            chatId,
            toolCallId: toolCall.toolCallId,
          },
          (page, input) => runPageTool(page, input, deps),
        ),
      });
    }
    const consoleText = formatConsoleEntries(
      getSandboxConsoleEntries().slice(startIndex),
    );
    if (result.type === "result") {
      return { result: result.value, console: consoleText };
    }
    return { error: [result.value, consoleText].filter(Boolean).join("\n") };
  } catch (err) {
    const consoleText = formatConsoleEntries(
      getSandboxConsoleEntries().slice(startIndex),
    );
    const message =
      err instanceof Error ? err.message : String(err ?? "Unknown error");
    return { error: [message, consoleText].filter(Boolean).join("\n") };
  }
}

export function useSandboxTools(
  submitToolResult: (toolCallId: string, output: unknown) => Promise<void>,
  deps: SandboxToolDeps,
): SandboxToolHandlers {
  const calls = useRef(new Map<string, CallRecord>());
  const depsRef = useRef(deps);
  depsRef.current = deps;

  /**
   * Submit with retries: the server is PARKED on this result, so a lost
   * POST may see no further feed activity to trigger the scan-time repost —
   * the retry must be self-driven (Codex, #1474). A definitive refusal
   * (`CommandRefused`) stops immediately; the scan healing stays as the
   * backstop for reconnects.
   */
  const submitWithRetry = useCallback(
    async (toolCallId: string, output: unknown) => {
      for (let attempt = 0; ; attempt++) {
        try {
          return await submitToolResult(toolCallId, output);
        } catch (err) {
          if (err instanceof CommandRefused || attempt >= 4) throw err;
          await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
        }
      }
    },
    [submitToolResult],
  );

  const runToolCall = useCallback(
    async (toolCall: PendingToolCall) => {
      const output = await executeToolCall(toolCall, depsRef.current);
      const record: CallRecord = { state: "answered", output, inFlight: true };
      calls.current.set(toolCall.toolCallId, record);
      void submitWithRetry(toolCall.toolCallId, output)
        .catch((err) => console.warn("tool result post failed", err))
        .finally(() => {
          record.inFlight = false;
        });
    },
    [submitWithRetry],
  );

  const onMessages = useCallback(
    (messages: { parts?: unknown[] }[]) => {
      for (const call of pendingToolCalls(messages)) {
        const prior = calls.current.get(call.toolCallId);
        if (prior?.state === "running") continue;
        if (prior?.state === "answered") {
          // Feed still shows the part unresolved: the POST was lost — heal it.
          if (!prior.inFlight) {
            prior.inFlight = true;
            void submitWithRetry(call.toolCallId, prior.output)
              .catch((err) => console.warn("tool result repost failed", err))
              .finally(() => {
                prior.inFlight = false;
              });
          }
          continue;
        }
        calls.current.set(call.toolCallId, { state: "running" });
        void runToolCall(call).catch((err) => {
          console.warn("tool call failed", err);
          calls.current.delete(call.toolCallId);
        });
      }
    },
    [runToolCall, submitWithRetry],
  );

  // Stable identity — consumers hang effects off this object.
  return useMemo(() => ({ onMessages }), [onMessages]);
}
